/* Project Botanical — Cinematic interactions
   Custom cursor, magnetic buttons, scroll-driven hero, parallax,
   split-text reveal, fluid mouse parallax. Runs after GSAP loads. */

(function () {
  'use strict';

  const ready = (fn) =>
    document.readyState !== 'loading'
      ? fn()
      : document.addEventListener('DOMContentLoaded', fn);

  const waitForGsap = () =>
    new Promise((resolve) => {
      if (window.gsap && window.ScrollTrigger) return resolve();
      let tries = 0;
      const t = setInterval(() => {
        tries++;
        if (window.gsap && window.ScrollTrigger) {
          clearInterval(t);
          resolve();
        } else if (tries > 80) {
          clearInterval(t);
          resolve(); // give up — degrade gracefully
        }
      }, 50);
    });

  const lerp = (a, b, n) => a + (b - a) * n;
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const isFinePointer = window.matchMedia('(hover:hover) and (pointer:fine)').matches;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  ready(async () => {
    await waitForGsap();
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }

    initMagnetic();
    initHeroScroll();
    initHeroParallax();
    initSplitTextReveal();
    initImageParallax();
    initSectionFadeIn();
    initPdpCineHeroScroll();
    initPdpCineStickyCart();
    initPdpCineQty();
  });

  /* ============================================================
   * Custom cursor (dot + ring)
   * ============================================================ */
  function initCursor() {
    const cursor = document.querySelector('[data-cursor]');
    if (!cursor || !isFinePointer || reduceMotion) {
      if (cursor) cursor.remove();
      return;
    }
    document.documentElement.classList.add('pb-has-cursor');

    const dot = cursor.querySelector('.pb-cursor-dot');
    const ring = cursor.querySelector('.pb-cursor-ring');

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let dx = mx, dy = my;
    let rx = mx, ry = my;
    let scale = 1;
    let curScale = 1;

    window.addEventListener('mousemove', (e) => {
      mx = e.clientX;
      my = e.clientY;
    });

    document.addEventListener('mouseleave', () => cursor.classList.add('is-hidden'));
    document.addEventListener('mouseenter', () => cursor.classList.remove('is-hidden'));

    const hoverSelectors = 'a, button, [data-magnetic], [data-cursor-hover]';
    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(hoverSelectors)) {
        scale = 1.8;
        cursor.classList.add('is-hover');
      }
    });
    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(hoverSelectors)) {
        scale = 1;
        cursor.classList.remove('is-hover');
      }
    });

    function tick() {
      dx = lerp(dx, mx, 0.45);
      dy = lerp(dy, my, 0.45);
      rx = lerp(rx, mx, 0.16);
      ry = lerp(ry, my, 0.16);
      curScale = lerp(curScale, scale, 0.18);

      dot.style.transform = `translate3d(${dx}px, ${dy}px, 0) translate(-50%, -50%)`;
      ring.style.transform = `translate3d(${rx}px, ${ry}px, 0) translate(-50%, -50%) scale(${curScale})`;
      requestAnimationFrame(tick);
    }
    tick();
  }

  /* ============================================================
   * Magnetic buttons / links
   * ============================================================ */
  function initMagnetic() {
    if (!isFinePointer || reduceMotion) return;
    const els = document.querySelectorAll('[data-magnetic]');
    els.forEach((el) => {
      const strength = parseFloat(el.dataset.magneticStrength) || 22;
      let raf;
      let tx = 0, ty = 0, cx = 0, cy = 0;

      function loop() {
        cx = lerp(cx, tx, 0.18);
        cy = lerp(cy, ty, 0.18);
        el.style.transform = `translate(${cx}px, ${cy}px)`;
        if (Math.abs(cx - tx) > 0.05 || Math.abs(cy - ty) > 0.05) {
          raf = requestAnimationFrame(loop);
        } else {
          raf = null;
        }
      }

      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const relX = (e.clientX - r.left) / r.width - 0.5;
        const relY = (e.clientY - r.top) / r.height - 0.5;
        tx = relX * strength;
        ty = relY * strength;
        if (!raf) raf = requestAnimationFrame(loop);
      });

      el.addEventListener('mouseleave', () => {
        tx = 0; ty = 0;
        if (!raf) raf = requestAnimationFrame(loop);
      });
    });
  }

  /* ============================================================
   * Hero scroll-driven scale + blur + fade
   * ============================================================ */
  function initHeroScroll() {
    if (!window.gsap || !window.ScrollTrigger) return;
    const hero = document.querySelector('[data-cine-hero]');
    if (!hero) return;
    const media = hero.querySelector('[data-cine-media]');
    const content = hero.querySelector('[data-cine-content]');
    const video = hero.querySelector('[data-cine-video]');

    // Make the hero the scrub target (hero is sticky-like via CSS)
    const tl = window.gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: 0.6,
      },
    });

    tl.to(media, { scale: 1.06, ease: 'none' }, 0)
      .to(content, { y: -60, opacity: 0, ease: 'none' }, 0);

    // Slow down playback as user scrolls past hero (cinematic feel)
    if (video) {
      window.ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        onUpdate: (self) => {
          if (video.readyState >= 2) {
            video.playbackRate = clamp(1 - self.progress * 0.7, 0.3, 1);
          }
        },
      });
    }
  }

  /* ============================================================
   * Hero cursor parallax (subtle layered drift)
   * ============================================================ */
  function initHeroParallax() {
    if (reduceMotion) return;
    const hero = document.querySelector('[data-cine-hero]');
    if (!hero) return;
    const media = hero.querySelector('[data-cine-media]');
    const content = hero.querySelector('[data-cine-content]');
    if (!media || !content) return;

    let mx = 0, my = 0, cx = 0, cy = 0;

    hero.addEventListener('mousemove', (e) => {
      const r = hero.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    });
    hero.addEventListener('mouseleave', () => { mx = 0; my = 0; });

    function loop() {
      cx = lerp(cx, mx, 0.06);
      cy = lerp(cy, my, 0.06);
      media.style.setProperty('--mx', `${cx * 18}px`);
      media.style.setProperty('--my', `${cy * 12}px`);
      content.style.setProperty('--cx', `${cx * -10}px`);
      content.style.setProperty('--cy', `${cy * -6}px`);
      requestAnimationFrame(loop);
    }
    loop();
  }

  /* ============================================================
   * Split-text headline reveal
   * ============================================================ */
  function initSplitTextReveal() {
    const targets = document.querySelectorAll('[data-cine-split]');
    targets.forEach((el) => {
      if (el.dataset.split === 'done') return;
      const words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach((w, i) => {
        const span = document.createElement('span');
        span.className = 'pb-cine-word';
        span.style.setProperty('--i', i);
        const inner = document.createElement('span');
        inner.className = 'pb-cine-word-inner';
        inner.textContent = w;
        span.appendChild(inner);
        el.appendChild(span);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      el.dataset.split = 'done';
      // trigger reveal next frame
      requestAnimationFrame(() => el.classList.add('is-revealed'));
    });

    // Generic fade-up timing for siblings
    document.querySelectorAll('[data-cine-fade]').forEach((el) => {
      const i = parseInt(el.dataset.cineFade, 10) || 0;
      el.style.setProperty('--cine-fade-i', i);
      requestAnimationFrame(() => el.classList.add('is-revealed'));
    });
  }

  /* ============================================================
   * Section image parallax (subtle vertical drift on scroll)
   * ============================================================ */
  function initImageParallax() {
    if (!window.gsap || !window.ScrollTrigger || reduceMotion) return;
    document.querySelectorAll('[data-parallax]').forEach((el) => {
      const speed = parseFloat(el.dataset.parallax) || 0.15;
      window.gsap.to(el, {
        yPercent: -speed * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    });
  }

  /* ============================================================
   * Generic section fade-up via IntersectionObserver
   * ============================================================ */
  function initSectionFadeIn() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('pb-cine-in');
            io.unobserve(en.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.pb-section, .pb-featured, .pb-trust-bar, .pb-newsletter, .pb-edu-section, [data-cine-reveal]')
      .forEach((el) => {
        el.classList.add('pb-cine-pre');
        io.observe(el);
      });
  }

  /* ============================================================
   * PDP cinematic hero — scroll-driven scale + content fade
   * ============================================================ */
  function initPdpCineHeroScroll() {
    if (!window.gsap || !window.ScrollTrigger) return;
    const hero = document.querySelector('[data-pdp-cine-hero]');
    if (!hero) return;
    const media = hero.querySelector('[data-pdp-cine-media]');
    const content = hero.querySelector('[data-pdp-cine-content]');

    const tl = window.gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: 0.6,
      },
    });
    tl.to(media, { scale: 1.06, ease: 'none' }, 0)
      .to(content, { y: -60, opacity: 0, ease: 'none' }, 0);
  }

  /* ============================================================
   * PDP sticky cart — visible after scrolling past hero
   * ============================================================ */
  function initPdpCineStickyCart() {
    const sticky = document.querySelector('[data-pdp-cine-sticky]');
    const hero = document.querySelector('[data-pdp-cine-hero]');
    if (!sticky || !hero) return;

    function update() {
      const r = hero.getBoundingClientRect();
      sticky.classList.toggle('is-visible', r.bottom < window.innerHeight * 0.4);
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  /* ============================================================
   * PDP qty stepper (cinematic form)
   * ============================================================ */
  function initPdpCineQty() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-btn]');
      if (!btn) return;
      const wrap = btn.closest('[data-qty]');
      const input = wrap && wrap.querySelector('input[type="number"]');
      if (!input) return;
      let val = parseInt(input.value, 10) || 1;
      val += btn.dataset.qtyBtn === 'inc' ? 1 : -1;
      input.value = Math.max(1, val);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

})();
